import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { formatBJ } from '../utils/time';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout as AntLayout, Menu, Dropdown, Avatar, Button, theme, Breadcrumb, Badge, List, Tag, Empty, Popover, Spin,  Drawer, Modal, Input, Form, message, FloatButton } from 'antd';
import {
  DashboardOutlined,
  ShoppingOutlined,
  ShoppingCartOutlined,
  DatabaseOutlined,
  TeamOutlined,
  BarChartOutlined,
  FileTextOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  NotificationOutlined,
  SettingFilled,
  MenuOutlined,
  SafetyCertificateOutlined,
  AuditOutlined,
  CloudServerOutlined,
  VerticalAlignTopOutlined,
  VerticalAlignBottomOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { notificationAPI, authAPI, purchaseConfirmAPI } from '../api/client';
import useMobile from '../hooks/useMobile';

function ScrollToBottomButton() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      setVisible(scrollHeight - scrollTop - clientHeight > 300);
    };
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  if (!visible) return null;
  return (
    <FloatButton
      icon={<VerticalAlignBottomOutlined />}
      tooltip="滚动到底部"
      style={{ right: 24, bottom: 32 }}
      onClick={() => window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'smooth' })}
    />
  );
}

const { Header, Sider, Content, Footer } = AntLayout;

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [profileForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const role = user?.role || 'warehouse';
  const isMobile = useMobile();
  const [purchaseBadge, setPurchaseBadge] = useState(0);
  // 部署标记：前端构建时间 + 后端启动时间，显示在底部，用于确认线上是否最新代码
  const [deployInfo, setDeployInfo] = useState({ fe: '加载中…', be: '加载中…' });
  useEffect(() => {
    let alive = true;
    Promise.all([
      fetch('/buildinfo.json', { cache: 'no-store' }).then(r => r.json()).catch(() => ({})),
      fetch('/api/build-info', { cache: 'no-store' }).then(r => r.json()).catch(() => ({})),
    ]).then(([fe, be]) => {
      if (!alive) return;
      setDeployInfo({ fe: fe.ts || '未知', be: be.started_at || '未知' });
    });
    return () => { alive = false; };
  }, []);

  // 根据角色动态计算菜单项
  const menuItems = useMemo(() => {
    // 通用菜单项
    const dashboard = { key: '/', icon: <DashboardOutlined />, label: '生产看板' };
    const orders = { key: '/customer-orders', icon: <FileTextOutlined />, label: '订单管理' };
    const purchaseLabel = purchaseBadge > 0 ? (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        采购管理
        <span style={{
          background: '#ff4d4f', color: '#fff', fontSize: 10,
          padding: '0 5px', borderRadius: 8, fontWeight: 600,
          lineHeight: '16px', minWidth: 16, textAlign: 'center',
          animation: 'badge-pulse 1.5s ease-in-out infinite',
        }}>{purchaseBadge}</span>
      </span>
    ) : '采购管理';
    const purchases = { key: '/purchases', icon: <ShoppingCartOutlined />, label: purchaseLabel };
    const inventory = { key: '/inventory', icon: <DatabaseOutlined />, label: '库存管理' };
    const reports = { key: '/reports', icon: <BarChartOutlined />, label: '报表' };
    const customization = { key: '/customization', icon: <AppstoreOutlined />, label: '定制模板' };

    // 设置子菜单 - 所有角色都能看到通知中心
    const settingsChildren = [
      { key: '/suppliers', icon: <TeamOutlined />, label: '供应商管理' },
      { key: '/notifications', icon: <NotificationOutlined />, label: '通知中心' },
    ];

    // admin 额外子菜单项
    if (role === 'admin') {
      settingsChildren.push(
        { key: '/users', icon: <SafetyCertificateOutlined />, label: '用户管理' },
        { key: '/server-status', icon: <CloudServerOutlined />, label: '服务器状态' },
        { key: '/settings', icon: <SettingFilled />, label: '系统设置' },
      );
    }

    // admin + warehouse 可看操作日志
    if (role === 'admin' || role === 'warehouse') {
      settingsChildren.push(
        { key: '/operation-logs', icon: <AuditOutlined />, label: '操作日志' },
      );
    }

    const settings = {
      key: 'settings-group',
      icon: <SettingFilled />,
      label: '设置',
      children: settingsChildren,
    };

    if (role === 'customer') {
      // customer: 订单管理 + 采购管理 + 设置(只有通知中心)
      return [
        orders,
        purchases,
        {
          ...settings,
          children: [{ key: '/notifications', icon: <NotificationOutlined />, label: '通知中心' }],
        },
      ];
    }

    // admin: 全部菜单(包含用户管理+操作日志), warehouse: 生产看板+订单管理+采购管理+库存管理+报表+设置(供应商管理+通知中心)
    if (role === 'admin') {
      return [dashboard, orders, purchases, customization, inventory, reports, settings];
    }

    // warehouse: 无用户管理和操作日志
    return [
      dashboard, orders, purchases, inventory, reports,
      {
        ...settings,
        children: [
          { key: '/suppliers', icon: <TeamOutlined />, label: '供应商管理' },
          { key: '/notifications', icon: <NotificationOutlined />, label: '通知中心' },
        ],
      },
    ];
  }, [role, purchaseBadge]);
  const breadcrumbNameMap = useMemo(() => ({
    '/': '生产看板',
    '/products': '产品管理',
    '/customer-orders': '订单管理',
    '/purchases': '采购管理',
    '/inventory': '库存管理',
    '/reports': '报表',
    '/settings': '设置',
    '/suppliers': '供应商管理',
    '/notifications': '通知中心',
    '/users': '用户管理',
    '/operation-logs': '操作日志',
    '/customization': '定制模板',
    '/order/:orderId/customize': '买家定制',
  }), []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [apiUnreadCount, setApiUnreadCount] = useState(0);
  const [popoverNotifs, setPopoverNotifs] = useState([]);
  const [popoverLoading, setPopoverLoading] = useState(false);
  const popoverFetchedRef = useRef(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await notificationAPI.unreadCount();
      setApiUnreadCount(res.data.unread_count || 0);
    } catch(e) { /* ignore */ }
  }, []);

  const fetchPurchaseBadge = useCallback(async () => {
    try {
      const res = await purchaseConfirmAPI.pendingSummary();
      const d = res.data;
      if (role === 'customer') {
        setPurchaseBadge(d.total_customer_pending || 0);
      } else {
        setPurchaseBadge(d.total_supplier_pending || 0);
      }
    } catch(e) { /* ignore */ }
  }, [role]);

  useEffect(() => {
    fetchUnreadCount();
    fetchPurchaseBadge();
    const interval = setInterval(() => { fetchUnreadCount(); fetchPurchaseBadge(); }, 10000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchPurchaseBadge]);

  const fetchPopoverNotifs = useCallback(async () => {
    setPopoverLoading(true);
    try {
      const res = await notificationAPI.list({ page: 1, page_size: 10 });
      setPopoverNotifs(res.data.items || []);
    } catch(e) { /* ignore */ }
    finally { setPopoverLoading(false); }
  }, []);

  const handleBellClick = () => {
    fetchPopoverNotifs();
  };

  const handlePopoverRead = async (id) => {
    try {
      await notificationAPI.markRead(id);
      setPopoverNotifs(prev => prev.map(n => n.id === id ? { ...n, status: 'read' } : n));
      fetchUnreadCount();
    } catch(e) { /* ignore */ }
  };

  const notificationContent = (
    <div style={{ width: isMobile ? 300 : 380, maxHeight: 420, overflowY: 'auto' }}>
      {popoverLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
      ) : popoverNotifs.length === 0 ? (
        <Empty description="暂无通知" image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <>
          <List
            dataSource={popoverNotifs}
            size="small"
            renderItem={(item) => (
              <List.Item
                style={{
                  background: item.status === 'sent' ? '#e6f7ff' : 'transparent',
                  padding: '8px 12px',
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  handlePopoverRead(item.id);
                  navigate('/notifications');
                }}
              >
                <List.Item.Meta
                  title={
                    <span style={{ fontWeight: item.status === 'sent' ? 600 : 400 }}>
                      {item.title}
                    </span>
                  }
                  description={
                    <div>
                      <Tag color={
                        item.type === 'pause' ? 'orange' :
                        item.type === 'urgent' ? 'red' :
                        item.type === 'cancel' ? 'red' :
                        item.type === 'stock_warning' ? 'gold' :
                        'blue'
                      } style={{ marginRight: 4 }}>
                        {item.type === 'new_order' ? '新订单' :
                         item.type === 'pause' ? '暂停' :
                         item.type === 'resume' ? '恢复' :
                         item.type === 'cancel' ? '取消' :
                         item.type === 'urgent' ? '加急' :
                         item.type === 'complete' ? '完成' :
                         item.type === 'stock_warning' ? '库存预警' :
                         item.type === 'note' ? '备注' : item.type}
                      </Tag>
                      <span style={{ color: '#999', fontSize: 12 }}>
                        {item.created_at ? formatBJ(item.created_at) : ''}
                      </span>
                    </div>
                  }
                />
              </List.Item>
            )}
          />
          <div style={{ textAlign: 'center', borderTop: '1px solid #f0f0f0', paddingTop: 8, marginTop: 4 }}>
            <Button type="link" onClick={() => navigate('/notifications')}>查看全部通知</Button>
          </div>
        </>
      )}
    </div>
  );
  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const handleMenuClick = ({ key }) => {
    navigate(key);
    // 移动端点击菜单后关闭抽屉
    if (isMobile) setDrawerOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: '设置',
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }) => {
    if (key === 'logout') {
      handleLogout();
    } else if (key === 'profile') {
      profileForm.resetFields();
      setProfileModalVisible(true);
    }
  };

  const handleChangePassword = async () => {
    try {
      const values = await profileForm.validateFields();
      setPasswordLoading(true);
      await authAPI.updateMe({
        old_password: values.old_password,
        password: values.new_password,
      });
      message.success('密码修改成功');
      setProfileModalVisible(false);
      profileForm.resetFields();
    } catch (err) {
      // 先检查是否是表单验证错误（antd Form validateFields 抛出）
      if (err.errorFields) {
        return; // 表单校验失败，antd 自动提示
      }
      if (err.response) {
        message.error(err.response?.data?.detail || '密码修改失败');
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  // Build breadcrumb from path
  const pathSnippets = location.pathname.split('/').filter((i) => i);
  const breadcrumbItems = [
    { title: '首页' },
    ...pathSnippets.map((_, index) => {
      const url = `/${pathSnippets.slice(0, index + 1).join('/')}`;
      return { title: breadcrumbNameMap[url] || pathSnippets[index] };
    }),
  ];

  const displayName = user?.name || user?.username || user?.displayName || '用户';

  // 侧边栏菜单内容（桌面端和移动端共用）
  const siderMenuContent = (
    <>
      <div
        style={{
          height: 64,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <div
          style={{
            color: '#fff',
            fontSize: (isMobile && !collapsed) ? 18 : collapsed ? 16 : 18,
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            letterSpacing: 1,
          }}
        >
          {(isMobile || collapsed) ? '进销存' : '供应商进销存'}
        </div>
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={handleMenuClick}
      />
    </>
  );

  return (
    <AntLayout style={{ minHeight: '100vh' }}>
      {/* 桮面端：固定侧边栏 */}
      {!isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{
            overflow: 'auto',
            height: '100vh',
            position: 'fixed',
            left: 0,
            top: 0,
            bottom: 0,
            zIndex: 10,
          }}
        >
          {siderMenuContent}
        </Sider>
      )}

      {/* 移动端：Drawer抽屉侧边栏 */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={220}
          styles={{ body: { padding: 0, background: '#001529' }, header: { display: 'none' } }}
          closable={false}
        >
          <div style={{ background: '#001529', height: '100%' }}>
            {siderMenuContent}
          </div>
        </Drawer>
      )}

      <AntLayout style={{ marginLeft: isMobile ? 0 : (collapsed ? 80 : 200), transition: isMobile ? 'none' : 'margin-left 0.2s' }}>
        <Header
          style={{
            padding: isMobile ? '0 12px' : '0 24px',
            background: colorBgContainer,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 9,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerOpen(true)}
                style={{ fontSize: 18, width: 40, height: 48 }}
              />
            ) : (
              <Button
                type="text"
                icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: 16, width: 48, height: 48 }}
              />
            )}
            {!isMobile && <Breadcrumb items={breadcrumbItems} />}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 16 }}>
            <Popover
              content={notificationContent}
              title="系统通知"
              trigger="click"
              placement="bottomRight"
              onOpenChange={(open) => { if (open) handleBellClick(); }}
            >
              <Badge count={apiUnreadCount} size="small" overflowCount={99}>
                <Button type="text" icon={<BellOutlined />} style={{ fontSize: 16 }} />
              </Badge>
            </Popover>
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: borderRadiusLG,
                  transition: 'background 0.2s',
                }}
              >
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: '#1677ff' }}
                />
                {!isMobile && <span>{displayName}</span>}
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: isMobile ? '8px' : '16px 24px',
            padding: 0,
            minHeight: 280,
            background: '#f5f7fa',
            borderRadius: borderRadiusLG,
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Content>
        {!isMobile && (
          <Footer style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>
            供应商进销存管理系统 &copy; {new Date().getFullYear()}
            <span style={{ marginLeft: 12, color: '#bbb' }}>
              前端构建: {deployInfo.fe} · 后端启动: {deployInfo.be}
            </span>
          </Footer>
        )}
      </AntLayout>

      {/* 修改密码弹窗 */}
      <Modal
        title="修改密码"
        open={profileModalVisible}
        onCancel={() => setProfileModalVisible(false)}
        onOk={handleChangePassword}
        confirmLoading={passwordLoading}
        okText="确认修改"
        cancelText="取消"
      >
        <Form form={profileForm} layout="vertical">
          <Form.Item
            name="old_password"
            label="旧密码"
            rules={[{ required: true, message: '请输入旧密码' }]}
          >
            <Input.Password placeholder="请输入旧密码" />
          </Form.Item>
          <Form.Item
            name="new_password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 6, message: '密码至少6个字符' },
            ]}
          >
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
          <Form.Item
            name="confirm_password"
            label="确认新密码"
            dependencies={['new_password']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('new_password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password placeholder="再次输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
      <FloatButton.BackTop visibilityHeight={300} style={{ right: 24, bottom: 80 }} />
      <ScrollToBottomButton />
    </AntLayout>
  );
}
